import { isValidPhoneNumber } from 'libphonenumber-js';

import { defaultCountryCode } from '@components/country-code/constants';
import { CountryData } from '@components/country-code/types';
import {
  AddMemberFormDataType,
  AddMemberModalType,
  MemberListType
} from '@types';
import {
  isInvalidAmount,
  isInvalidEmail,
  isInvalidMemberName,
  isInvalidPincode
} from '@utils/validations';

export const hasChangesInMemberDetails = (
  formData: AddMemberFormDataType,
  selectedmember?: MemberListType | null
) => {
  const {
    name,
    primaryCountryCode,
    primaryNumber,
    altCountryCode,
    altNumber,
    amount,
    dueDate,
    startDate,
    email,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    pincode,
    termsList,
    endDate,
    totalFee,
    dateOfBirth,
    bloodGroup,
    guardianName,
    admissionNumber,
    admissionDate,
    counsellorName,
    isInsured,
    discount
  } = formData;

  const existingDiscount = selectedmember?.discount?.[0];
  const hasDiscountChanged =
    (discount?.isEnabled || false) !== !!existingDiscount ||
    (discount?.isEnabled &&
      (discount.label !== (existingDiscount?.label ?? '') ||
        Number(discount.value || 0) !== Number(existingDiscount?.value ?? 0)));

  return (
    selectedmember?.name !== name ||
    selectedmember?.primaryNumber !== primaryNumber ||
    selectedmember.altNumber !== altNumber ||
    selectedmember.amount !== amount ||
    selectedmember.dueDate !== dueDate.day ||
    selectedmember.interval !== dueDate.interval ||
    selectedmember.paymentCollectionType !== dueDate.paymentCollectionType ||
    selectedmember.startDate !== startDate ||
    selectedmember.altNumberCountryCode !== altCountryCode ||
    selectedmember.primaryNumberCountryCode !== primaryCountryCode ||
    selectedmember.email !== email ||
    selectedmember.addressLine1 !== addressLine1 ||
    selectedmember.addressLine2 !== addressLine2 ||
    selectedmember.country !== country ||
    selectedmember.state !== state ||
    selectedmember.city !== city ||
    selectedmember.pincode !== pincode ||
    selectedmember.endDate !== endDate ||
    selectedmember.totalFee !== totalFee ||
    selectedmember.dateOfBirth !== dateOfBirth ||
    selectedmember.bloodGroup !== bloodGroup ||
    selectedmember.guardianName !== guardianName ||
    selectedmember.admissionNumber !== admissionNumber ||
    selectedmember.admissionDate !== admissionDate ||
    selectedmember.counsellorName !== counsellorName ||
    selectedmember.isInsured !== isInsured ||
    hasDiscountChanged ||
    selectedmember.termsList?.some(
      (term, index) =>
        termsList[index].dueAmount !== term.dueAmount ||
        termsList[index].dueDate !== term.dueDate ||
        termsList[index].feeCategoryData?.some(
          (category, catIndex) =>
            category.amount !== term.feeCategoryData?.[catIndex].amount
        )
    )
  );
};

export const isMemberSaveDisabled = (
  formData: AddMemberFormDataType,
  groupMemberMaxAmount: string,
  groupMemberMinAmount: string,
  memberNameMaxLength: string,
  memberNameMinLength: string,
  supported_phone_number_countries: any,
  isFeeCategoryEnabled: boolean,
  isCustomTerm: boolean = false,
  isAttendanceBasedFee: boolean = false
) => {
  const {
    amount,
    name,
    primaryCountryCode,
    primaryNumber,
    altCountryCode,
    altNumber,
    feeCategories,
    email,
    pincode,
    termsList,
    totalFee,
    endDate,
    startDate,
    discount
  } = formData;

  const getCountryId = (countryCode: string) =>
    (supported_phone_number_countries || []).find(
      (countryData: CountryData) => `${countryData.dial_code}` === countryCode
    )?.code;
  const areAllFeeCategoriesFilled =
    feeCategories.length > 0 &&
    feeCategories.every(
      (category: any) => category.value !== undefined && category.value !== ''
    );

  const isAmountInvalid =
    termsList?.length > 0
      ? termsList.some(
          (term) =>
            Number(term.dueAmount) !== 0 &&
            isInvalidAmount(
              term.dueAmount,
              groupMemberMaxAmount,
              groupMemberMinAmount,
              isCustomTerm
            )
        )
      : amount &&
        isInvalidAmount(
          amount,
          groupMemberMaxAmount,
          groupMemberMinAmount,
          isCustomTerm || isAttendanceBasedFee
        );

  const isDiscountInvalid =
    discount?.isEnabled &&
    (!discount.label.trim() ||
      !discount.value ||
      Number(discount.value) <= 0 ||
      Number(discount.value) > Number(amount || 0));

  return (
    isAmountInvalid ||
    isInvalidMemberName(name, memberNameMaxLength, memberNameMinLength) ||
    !isValidPhoneNumber(primaryNumber, getCountryId(primaryCountryCode)) ||
    !!(
      altNumber && !isValidPhoneNumber(altNumber, getCountryId(altCountryCode))
    ) ||
    `${altCountryCode}_${altNumber}` ===
      `${primaryCountryCode}_${primaryNumber}` ||
    (feeCategories.length > 0 &&
      isFeeCategoryEnabled &&
      !areAllFeeCategoriesFilled) ||
    !!(email && isInvalidEmail(email)) ||
    !!(pincode && isInvalidPincode(pincode)) ||
    (endDate && new Date(endDate) < new Date(startDate)) ||
    (totalFee && Number(totalFee) < Number(groupMemberMinAmount)) ||
    (totalFee && Number(totalFee) > Number(groupMemberMaxAmount)) ||
    isDiscountInvalid
  );
};

export const addMemberFormDataInitialState = {
  feeCategories: [],
  termsList: [],
  name: '',
  email: '',
  primaryCountryCode: defaultCountryCode,
  primaryNumber: '',
  altCountryCode: defaultCountryCode,
  altNumber: '',
  startDate: '',
  addressLine1: '',
  addressLine2: '',
  country: '',
  state: '',
  city: '',
  pincode: '',
  totalFee: '',
  dateOfBirth: '',
  bloodGroup: '',
  guardianName: '',
  admissionNumber: '',
  admissionDate: '',
  counsellorName: '',
  isInsured: 'false',
  discount: { isEnabled: false, label: '', value: '' }
};

export const getFormattedAddMemberData = (
  formData: AddMemberFormDataType,
  type: AddMemberModalType,
  memberId: string,
  id: string,
  groupAmount?: string,
  groupDueDate?: string
) => {
  const formattedDuesDate = () => {
    const dueDateValue = formData.dueDate.day || groupDueDate || '';
    return dueDateValue;
  };
  return {
    primaryNumber: formData.primaryNumber,
    name: formData.name,
    altNumber: formData.altNumber || '',
    amount: formData.amount || groupAmount || '',
    totalFee: formData.totalFee || '',
    dueDate: formattedDuesDate(),
    paymentCollectionType: formData.dueDate.paymentCollectionType,
    interval: formData.dueDate.interval,
    feeCategoryData: formData.feeCategories,
    id:
      type === AddMemberModalType.EDIT
        ? memberId
        : `member_${id}_${formData.name}${formData.primaryCountryCode}${formData.primaryNumber}`,
    primaryNumberCountryCode: formData.primaryCountryCode,
    altNumberCountryCode: formData.altCountryCode,
    startDate: formData.startDate,
    endDate: formData.endDate,
    email: formData.email,
    addressLine1: formData.addressLine1,
    addressLine2: formData.addressLine2,
    country: formData.country,
    state: formData.state,
    city: formData.city,
    pincode: formData.pincode,
    termsList: formData.termsList,
    dateOfBirth: formData.dateOfBirth,
    bloodGroup: formData.bloodGroup,
    guardianName: formData.guardianName,
    admissionNumber: formData.admissionNumber,
    admissionDate: formData.admissionDate,
    counsellorName: formData.counsellorName,
    isInsured: formData.isInsured,
    ...(formData.discount?.isEnabled &&
      formData.discount.label.trim() &&
      Number(formData.discount.value) > 0 && {
        discount: [
          {
            label: formData.discount.label.trim(),
            type: 'flat',
            amount: String(Number(formData.discount.value))
          }
        ]
      })
  };
};
